import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../logo2.png';
import SidebarNav from "./SidebarNav";

const RecordIncome = () => {
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [client, setClient] = useState('');
    const [taxPercentage, setTaxPercentage] = useState('');
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    // Fetch the available bank accounts
    const fetchBankAccounts = async () => {
        const { data, error } = await supabase.from('bank_accounts').select('*');
        if (!error) {
            setBankAccounts(data);
        } else {
            console.error('Error fetching bank accounts:', error);
            setMessage('无法获取银行账户信息');
        }
    };

    const handleRecordIncome = async (e) => {
        e.preventDefault();

        // 输入验证
        if (!client || !amount || taxPercentage === '' || !selectedBankAccount) {
            setMessage('请填写所有必填项');
            return;
        }

        const incomeAmount = parseFloat(amount);
        const taxRate = parseFloat(taxPercentage);

        if (isNaN(incomeAmount) || incomeAmount <= 0) {
            setMessage('请输入有效的金额');
            return;
        }

        if (isNaN(taxRate) || taxRate < 0) {
            setMessage('请输入有效的税率');
            return;
        }

        // 计算税额和总收入
        const taxAmount = (incomeAmount * taxRate) / 100;
        const totalIncome = incomeAmount + taxAmount;

        // 获取所选银行账户
        const bankAccount = bankAccounts.find(account => account.id === selectedBankAccount);
        if (!bankAccount) {
            setMessage('请选择有效的银行账户');
            return;
        }

        // 显示确认框，确认用户输入的信息
        const confirmation = window.confirm(
            `请确认以下信息:\n\n` +
            `客户: ${client}\n` +
            `描述: ${description}\n` +
            `金额: ${incomeAmount.toFixed(2)}\n` +
            `税率: ${taxRate}%\n` +
            `税额: ${taxAmount.toFixed(2)}\n` +
            `总收入: ${totalIncome.toFixed(2)}\n` +
            `银行账户: ${bankAccount.bank_name} - ${bankAccount.account_number}\n` +
            `当前余额: ${bankAccount.balance.toFixed(2)}\n` +
            `增加后余额: ${(bankAccount.balance + totalIncome).toFixed(2)}`
        );

        // 如果用户取消确认，不执行后续操作
        if (!confirmation) {
            return;
        }

        // 获取银行账户余额（重新获取以确保最新）
        const { data: bankData, error: bankError } = await supabase
            .from('bank_accounts')
            .select('balance')
            .eq('id', selectedBankAccount)
            .single();

        if (bankError || !bankData) {
            setMessage('获取银行账户余额时出错');
            return;
        }

        const currentBalance = parseFloat(bankData.balance);

        // 计算新的余额
        const newBalance = currentBalance + totalIncome;

        // 开始事务：插入交易记录并更新银行账户余额
        const { error: transactionError } = await supabase.from('transactions').insert([
            {
                type: '收入',
                transaction_type: '公司收入',
                amount: incomeAmount,
                tax_percentage: taxRate,
                tax_amount: taxAmount,
                description: `收入来自 ${client}: ${description}`,
                bank_account_id: selectedBankAccount,
                date: new Date(),
            }
        ]);

        if (transactionError) {
            console.error('Transaction Error:', transactionError);
            setMessage('记录收入时出错');
            return;
        }

        // 更新银行账户余额
        const { error: updateError } = await supabase
            .from('bank_accounts')
            .update({ balance: newBalance })
            .eq('id', selectedBankAccount);

        if (updateError) {
            console.error('Update Error:', updateError);
            setMessage('更新银行账户余额时出错');
            return;
        }

        setMessage(`已记录收入 ${incomeAmount.toFixed(2)} 元，税额 ${taxAmount.toFixed(2)} 元。新的银行账户余额为 ${newBalance.toFixed(2)} 元。`);

        // 重置表单
        setAmount('');
        setTaxPercentage('');
        setDescription('');
        setClient('');
        setSelectedBankAccount('');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SidebarNav />

            {/* Main Content */}
            <div className="lg:pl-64">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <img
                                src={logo}
                                alt="Kindbridge Investments"
                                className="h-12 w-auto"
                            />
                        </div>
                        <h1 className="mt-6 text-2xl font-semibold text-gray-900">
                            记录收入
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            请填写以下表单记录新的收入
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                        <form onSubmit={handleRecordIncome} className="p-6 space-y-6">
                            {/* Client */}
                            <div>
                                <label htmlFor="client" className="block text-sm font-medium text-gray-700">
                                    客户<span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    id="client"
                                    type="text"
                                    placeholder="请输入客户名称"
                                    value={client}
                                    onChange={(e) => setClient(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                    描述<span className="text-red-500 ml-1">*</span>
                                </label>
                                <textarea
                                    id="description"
                                    placeholder="请输入详细描述"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={4}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                />
                            </div>

                            {/* Amount and Tax Rate */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                                        金额<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="amount"
                                            type="number"
                                            placeholder="0.00"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="tax-percentage" className="block text-sm font-medium text-gray-700">
                                        税率<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="tax-percentage"
                                            type="number"
                                            placeholder="0.00, 无税填0"
                                            value={taxPercentage}
                                            onChange={(e) => setTaxPercentage(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bank Account Selection */}
                            <div>
                                <label htmlFor="bank-account" className="block text-sm font-medium text-gray-700">
                                    银行账户<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    id="bank-account"
                                    value={selectedBankAccount}
                                    onChange={(e) => setSelectedBankAccount(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                >
                                    <option value="">请选择银行账户</option>
                                    {bankAccounts.map((account) => (
                                        <option key={account.id} value={account.id}>
                                            {account.bank_name} - {account.account_number} (余额: {parseFloat(account.balance).toFixed(2)})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                    提交收入
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`mt-4 p-4 rounded-md ${
                            message.includes('成功')
                                ? 'bg-green-50 text-green-800'
                                : 'bg-red-50 text-red-800'
                        }`}>
                            <p className="text-sm">{message}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export default RecordIncome;
